import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { pricingData } from '../data/pricing';
import { products } from '../data/products';
export function PricingPage() {
  const [selectedProductId, setSelectedProductId] = useState(products[0].id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const selectedPricing = pricingData.find(p => p.productId === selectedProductId);
  if (!selectedProduct || !selectedPricing) return null;
  return <div className="pt-24 pb-16" data-id="element-348">
      <section className="bg-white pb-12" data-id="element-349">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-350">
          <SectionHeading eyebrow="Pricing" title="Simple, Transparent Pricing" description="Choose the perfect plan for your needs. No hidden fees." className="mb-12" data-id="element-351" />

          {/* Product Selector - Desktop Tabs */}
          <div className="hidden md:flex justify-center mb-16" data-id="element-352">
            <div className="inline-flex bg-slate-100 p-1.5 rounded-xl" data-id="element-353">
              {products.map(product => <button key={product.id} onClick={() => setSelectedProductId(product.id)} className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedProductId === product.id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-354">
                  {selectedProductId === product.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-lg shadow-sm" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-355" />}
                  <span className="relative z-10" data-id="element-356">{product.name}</span>
                </button>)}
            </div>
          </div>

          {/* Product Selector - Mobile Dropdown */}
          <div className="md:hidden mb-12 relative max-w-xs mx-auto" data-id="element-357">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full flex items-center justify-between bg-white border border-slate-200 px-4 py-3 rounded-xl shadow-sm text-left font-medium text-slate-900" data-id="element-358">
              <span data-id="element-359">{selectedProduct.name}</span>
              <ChevronDown size={20} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-360" />
            </button>

            <AnimatePresence data-id="element-361">
              {isDropdownOpen && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden" data-id="element-362">
                  {products.map(product => <button key={product.id} onClick={() => {
                setSelectedProductId(product.id);
                setIsDropdownOpen(false);
              }} className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors ${selectedProductId === product.id ? 'bg-navy-50 text-navy-600 font-medium' : 'text-slate-600'}`} data-id="element-363">
                      {product.name}
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="bg-slate-50 py-16" data-id="element-364">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-365">
          <AnimatePresence mode="wait" data-id="element-366">
            <motion.div key={selectedProductId} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="grid md:grid-cols-3 gap-8 items-start" data-id="element-367">
              {selectedPricing.tiers.map((tier, index) => <div key={index} className={`bg-white rounded-2xl p-8 border ${tier.highlighted ? 'border-navy-600 shadow-xl scale-105 z-10 relative' : 'border-slate-200 shadow-sm'}`} data-id="element-368">
                  {tier.highlighted && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-navy-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide" data-id="element-369">
                      Most Popular
                    </div>}

                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-370">
                    {tier.name}
                  </h3>
                  <div className="text-4xl font-bold text-slate-900 mb-4" data-id="element-371">
                    {tier.price}
                  </div>
                  <p className="text-slate-600 mb-8 text-sm" data-id="element-372">
                    {tier.description}
                  </p>

                  <Button variant={tier.highlighted ? 'primary' : 'outline'} fullWidth href="/contact" className="mb-8" data-id="element-373">
                    {tier.ctaText}
                  </Button>

                  <ul className="space-y-4" data-id="element-374">
                    {tier.features.map((feature, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-375">
                        <Check size={18} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-376" />
                        {feature}
                      </li>)}
                  </ul>
                </div>)}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Bundle CTA */}
      <section className="py-20 bg-white text-center" data-id="element-377">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-378">
          <div className="bg-navy-900 rounded-3xl p-12 text-white" data-id="element-379">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-380">
              Need multiple products?
            </h2>
            <p className="text-navy-200 mb-8 text-lg" data-id="element-381">
              We offer special bundle pricing when you combine 3 or more
              products from the NexusFlow ecosystem.
            </p>
            <Button variant="secondary" href="/contact" data-id="element-382">
              Contact Us for Bundle Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white" data-id="element-383">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-384">
          <SectionHeading title="Pricing FAQ" className="mb-12" data-id="element-385" />
          <Accordion items={[{
          question: 'Can I switch plans later?',
          answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.'
        }, {
          question: 'Do you offer annual discounts?',
          answer: 'Yes, we offer a 20% discount on all plans when billed annually.'
        }, {
          question: 'Is there a free trial?',
          answer: 'All Starter and Growth plans come with a 14-day free trial. No credit card required to start.'
        }, {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, Amex) and bank transfers for Enterprise plans.'
        }]} data-id="element-386" />
        </div>
      </section>
    </div>;
}